import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollSmoother } from "gsap/ScrollSmoother";

// Register plugins
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, ScrollSmoother);

// Expose globals for other scripts
window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;
window.ScrollToPlugin = ScrollToPlugin;

// Fire a "gsap-ready" event immediately (so other scripts can wait for it)
document.dispatchEvent(new CustomEvent("longevity:gsap-ready"));

// Keep pre-hidden animated elements hidden until page scripts initialize.
const markAnimationsReady = () => {
  document.documentElement.classList.add("anim-ready");
};

window.addEventListener(
  "load",
  () => {
    requestAnimationFrame(markAnimationsReady);
  },
  { once: true },
);

// Fallback: never leave content hidden if load event is delayed.
setTimeout(markAnimationsReady, 1800);

// -----------------------------
// ScrollTrigger refresh (SAFE)
// -----------------------------
let refreshQueued = false;

function queueScrollTriggerRefresh() {
  if (!window.ScrollTrigger) return;
  if (refreshQueued) return;

  refreshQueued = true;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      refreshQueued = false;

      try {
        if (window.ScrollTrigger.isRefreshing) return;
        // IMPORTANT: do NOT call refresh(true)
        window.ScrollTrigger.refresh();
      } catch (e) {
        // IMPORTANT: do NOT kill triggers here.
        // Killing triggers during refresh cycles is what leads to "reading end" crashes.
        // If you want debug info:
        // console.warn("ScrollTrigger.refresh failed:", e);
      }
    });
  });
}

// -----------------------------
// Main init
// -----------------------------
document.addEventListener("DOMContentLoaded", () => {
  const header = document.getElementById("site-header");
  const toggle = document.querySelector(".mobile-toggle");
  const drawer = document.getElementById("mobile-drawer");
  const overlay = document.querySelector(".drawer-overlay");
  const closeBtns = document.querySelectorAll("[data-drawer-close]");

  // Header entrance + scroll hide/show
  if (header) {
    const syncHeaderHeight = () => {
      document.documentElement.style.setProperty(
        "--site-header-height",
        `${header.offsetHeight}px`,
      );
    };

    syncHeaderHeight();
    window.addEventListener("resize", syncHeaderHeight);

    header.classList.add("is-ready");
    header.classList.add("is-entering");

    gsap.to(header, {
      opacity: 1,
      y: 0,
      duration: 0.55,
      ease: "power3.out",
      onComplete: () => {
        header.classList.remove("is-entering");
        gsap.set(header, { clearProps: "transform,opacity" });
        syncHeaderHeight();
      },
    });
  }

  let scrollSmoother = null;
  const headerHideAfter = 80;

  const updateHeaderOnScroll = (scrollY, direction = 0) => {
    if (!header) return;

    const currentY = Math.max(0, scrollY);

    header.classList.toggle("is-scrolled", currentY > 12);

    if (document.body.classList.contains("mobile-menu-open")) {
      header.classList.remove("is-hidden");
      return;
    }

    if (currentY <= headerHideAfter) {
      header.classList.remove("is-hidden");
      return;
    }

    if (direction === 1) {
      header.classList.add("is-hidden");
    } else if (direction === -1) {
      header.classList.remove("is-hidden");
    }
  };

  // Language dropdown
  document.querySelectorAll("[data-nl-lang]").forEach((root) => {
    const toggle = root.querySelector("[data-nl-lang-toggle]");
    const menu = root.querySelector("[data-nl-lang-menu]");
    if (!toggle || !menu) return;

    const close = () => {
      root.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
      menu.hidden = true;
    };

    const open = () => {
      document.querySelectorAll("[data-nl-lang].is-open").forEach((other) => {
        if (other !== root) {
          other.classList.remove("is-open");
          const otherToggle = other.querySelector("[data-nl-lang-toggle]");
          const otherMenu = other.querySelector("[data-nl-lang-menu]");
          if (otherToggle) otherToggle.setAttribute("aria-expanded", "false");
          if (otherMenu) otherMenu.hidden = true;
        }
      });

      root.classList.add("is-open");
      toggle.setAttribute("aria-expanded", "true");
      menu.hidden = false;
    };

    toggle.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      root.classList.contains("is-open") ? close() : open();
    });

    document.addEventListener("click", (e) => {
      if (!root.contains(e.target)) close();
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") close();
    });
  });

  // Drawer behavior
  if (drawer && overlay) {
    // Start hidden
    gsap.set(drawer, { x: () => -drawer.offsetWidth, clearProps: "transform" });
    gsap.set(overlay, { opacity: 0, pointerEvents: "none" });

    let isOpen = false;
    let anim = null;

    const setOpenState = (open) => {
      document.body.classList.toggle("mobile-menu-open", open);
      drawer.setAttribute("aria-hidden", open ? "false" : "true");
      overlay.setAttribute("aria-hidden", open ? "false" : "true");

      if (toggle) {
        toggle.setAttribute("aria-expanded", open ? "true" : "false");
        toggle.classList.toggle("is-open", open);
      }
    };

    const openDrawer = () => {
      if (isOpen) return;
      isOpen = true;
      setOpenState(true);

      if (anim) anim.kill();

      const links = drawer.querySelectorAll("a");
      gsap.set(links, { opacity: 0, x: -10 });

      anim = gsap
        .timeline({ defaults: { ease: "power3.out" } })
        .set(overlay, { pointerEvents: "auto" }, 0)
        .to(overlay, { opacity: 1, duration: 0.2 }, 0)
        .to(drawer, { x: 0, duration: 0.38 }, 0)
        .to(links, { opacity: 1, x: 0, duration: 0.25, stagger: 0.03 }, 0.12);
    };

    const closeDrawer = () => {
      if (!isOpen) return;
      isOpen = false;

      if (anim) anim.kill();

      const links = drawer.querySelectorAll("a");

      anim = gsap
        .timeline({
          defaults: { ease: "power2.inOut" },
          onComplete: () => {
            setOpenState(false);
            gsap.set(overlay, { pointerEvents: "none" });
          },
        })
        .to(links, { opacity: 0, x: -8, duration: 0.12, stagger: 0.015 }, 0)
        .to(drawer, { x: () => -drawer.offsetWidth, duration: 0.3 }, 0.03)
        .to(overlay, { opacity: 0, duration: 0.2 }, 0.08);
    };

    // Toggle button
    if (toggle) {
      toggle.addEventListener("click", (e) => {
        e.preventDefault();
        isOpen ? closeDrawer() : openDrawer();
      });
    }

    // Overlay + close buttons
    overlay.addEventListener("click", closeDrawer);
    closeBtns.forEach((btn) => btn.addEventListener("click", closeDrawer));

    // Escape closes
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeDrawer();
    });

    // Keep drawer hidden on desktop and fix width changes
    window.addEventListener("resize", () => {
      if (window.innerWidth > 1280) closeDrawer();
      if (!isOpen) gsap.set(drawer, { x: () => -drawer.offsetWidth });
    });

    // Smooth scroll to anchors
    document.addEventListener("click", (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;

      const href = link.getAttribute("href");
      if (!href || href === "#") return;

      const target = document.querySelector(href);
      if (!target) return;

      e.preventDefault();
      if (isOpen) closeDrawer();

      const offset = header?.offsetHeight || 0;

      gsap.to(window, {
        duration: 0.9,
        ease: "power2.out",
        scrollTo: { y: target, offsetY: offset + 10 },
      });
    });
  }

  // -----------------------------
  // ScrollSmoother (SAFE)
  // -----------------------------
  // Only create if markup exists:
  // <div id="smooth-wrapper"><div id="smooth-content">...</div></div>
  const wrapper = document.querySelector("#smooth-wrapper");
  const content = document.querySelector("#smooth-content");

  if (wrapper && content) {
    scrollSmoother = ScrollSmoother.create({
      wrapper: "#smooth-wrapper",
      content: "#smooth-content",
      smooth: 1.2,
      effects: true,
      smoothTouch: 0.1,
    });
  }

  if (header && window.ScrollTrigger) {
    ScrollTrigger.create({
      start: 0,
      end: "max",
      onUpdate: (self) => {
        updateHeaderOnScroll(self.scroll(), self.direction);
      },
    });

    updateHeaderOnScroll(ScrollTrigger.scroll(), 0);
  }

  // One refresh after first layout pass
  queueScrollTriggerRefresh();

  // Tell block scripts "scroll system is ready"
  document.dispatchEvent(new CustomEvent("longevity:scroll-ready"));
});

// -----------------------------
// Pinched-pill buttons (ppill)
// -----------------------------
function ppillBuildPath(W, H, lobeWidths, gap) {
  const r = H / 2;

  const lobes = [];
  let x = 0;
  for (const w of lobeWidths) {
    lobes.push({ x0: x, x1: x + w });
    x += w + gap;
  }

  const dip = H * 0.42;
  const lhs = Math.pow(gap / 2 + r, 2);
  let R = (lhs / dip + dip - 2 * r) / 2;
  if (!Number.isFinite(R) || R < 0.5) R = 0.5;

  let d = "";
  const first = lobes[0];
  const last = lobes[lobes.length - 1];

  d += `M ${first.x0 + r} 0 `;

  for (let i = 0; i < lobes.length; i++) {
    const L = lobes[i];

    if (i === lobes.length - 1) {
      d += `L ${L.x1 - r} 0 `;
    } else {
      const next = lobes[i + 1];
      const midX = (L.x1 + next.x0) / 2;
      const k = R - dip;

      const cap1 = { cx: L.x1 - r, cy: r };
      const cap2 = { cx: next.x0 + r, cy: r };
      const necktop = { cx: midX, cy: -k };

      let dx = necktop.cx - cap1.cx;
      let dy = necktop.cy - cap1.cy;
      let len = Math.sqrt(dx * dx + dy * dy);
      const Px = cap1.cx + (r * dx) / len;
      const Py = cap1.cy + (r * dy) / len;

      dx = necktop.cx - cap2.cx;
      dy = necktop.cy - cap2.cy;
      len = Math.sqrt(dx * dx + dy * dy);
      const Qx = cap2.cx + (r * dx) / len;
      const Qy = cap2.cy + (r * dy) / len;

      d += `L ${L.x1 - r} 0 `;
      d += `A ${r} ${r} 0 0 1 ${Px} ${Py} `;
      d += `A ${R} ${R} 0 0 0 ${Qx} ${Qy} `;
      d += `A ${r} ${r} 0 0 1 ${next.x0 + r} 0 `;
    }
  }

  d += `A ${r} ${r} 0 0 1 ${last.x1 - r} ${H} `;

  for (let i = lobes.length - 1; i >= 0; i--) {
    const L = lobes[i];

    if (i === 0) {
      d += `L ${L.x0 + r} ${H} `;
    } else {
      const prev = lobes[i - 1];
      const midX = (prev.x1 + L.x0) / 2;
      const k = R - dip;

      const cap1 = { cx: L.x0 + r, cy: r };
      const cap2 = { cx: prev.x1 - r, cy: r };
      const neckbot = { cx: midX, cy: H + k };

      let dx = neckbot.cx - cap1.cx;
      let dy = neckbot.cy - cap1.cy;
      let len = Math.sqrt(dx * dx + dy * dy);
      const Px = cap1.cx + (r * dx) / len;
      const Py = cap1.cy + (r * dy) / len;

      dx = neckbot.cx - cap2.cx;
      dy = neckbot.cy - cap2.cy;
      len = Math.sqrt(dx * dx + dy * dy);
      const Qx = cap2.cx + (r * dx) / len;
      const Qy = cap2.cy + (r * dy) / len;

      d += `L ${L.x0 + r} ${H} `;
      d += `A ${r} ${r} 0 0 1 ${Px} ${Py} `;
      d += `A ${R} ${R} 0 0 0 ${Qx} ${Qy} `;
      d += `A ${r} ${r} 0 0 1 ${prev.x1 - r} ${H} `;
    }
  }

  d += `A ${r} ${r} 0 0 1 ${first.x0 + r} 0 Z`;
  return d;
}

function ppillRender(el) {
  const styles = getComputedStyle(el);
  const H = parseFloat(styles.getPropertyValue("--pp-h")) || 52;
  const gap = parseFloat(styles.getPropertyValue("--pp-gap")) || 6;

  const left = el.querySelector(".ppill__slot--left");
  const right = el.querySelector(".ppill__slot--right");
  if (!left || !right) return;

  const midCount = Number.parseInt(el.dataset.ppillMid || "0", 10) || 0;
  const includeRightByLayout = (el.dataset.ppillIncludeRight || "1") === "1";

  // Right slot may hold text and/or an <img>; textContent ignores images, so detect img explicitly.
  const rightHasImg = !!right.querySelector("img");
  const rightSlotHasContent =
    right.textContent.trim() !== "" || rightHasImg;

  const includeRightLobe = includeRightByLayout ? true : rightSlotHasContent;

  // Left circle is only used for the "Nr. 1" variation (non-wavy).
  // Also: only allow circle if the label actually fits a circle.
  const wantsLeftCircle = left.classList.contains("is-circle");
  const rightPill = includeRightLobe && right.classList.contains("is-pill");

  // When a pill lobe has no text, keep a sensible default width (reference HTML behavior),
  // but only for non-wavy variants where the right lobe is still present.
  const defaultEmptyPillW = 220;

  const leftTextW = Math.ceil(left.scrollWidth);
  const leftCircle = wantsLeftCircle && leftTextW <= H + 6;
  // Empty left pill collapses to 0 width and breaks SVG geometry — keep at least lobe height.
  const leftW = leftCircle ? H : leftTextW === 0 ? H : leftTextW;
  const rightW = includeRightLobe
    ? rightPill
      ? Math.ceil(
          rightSlotHasContent
            ? right.scrollWidth
            : Math.max(defaultEmptyPillW, right.scrollWidth),
        )
      : H
    : 0;

  const lobes = [leftW];
  for (let i = 0; i < midCount; i++) lobes.push(H);
  if (includeRightLobe) lobes.push(rightW);

  const W = lobes.reduce((a, b) => a + b, 0) + gap * (lobes.length - 1);

  el.style.width = `${W}px`;
  el.style.height = `${H}px`;

  const svg = el.querySelector("svg.ppill__shape");
  const path = svg?.querySelector("path");
  if (!svg || !path) return;

  svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
  svg.setAttribute("preserveAspectRatio", "none");
  path.setAttribute("d", ppillBuildPath(W, H, lobes, gap));

  // Place slots at the center of their respective lobes.
  let x = 0;
  const centers = lobes.map((w) => {
    const c = x + w / 2;
    x += w + gap;
    return c;
  });

  left.style.position = "absolute";
  left.style.left = `${centers[0]}px`;
  left.style.top = "50%";
  left.style.transform = "translate(-50%, -50%)";
  left.style.width = `${leftW}px`;
  left.style.maxWidth = `${leftW}px`;

  if (includeRightLobe) {
    right.style.display = "";
    right.style.position = "absolute";
    right.style.left = `${centers[centers.length - 1]}px`;
    right.style.top = "50%";
    right.style.transform = "translate(-50%, -50%)";
    right.style.width = `${rightW}px`;
    right.style.maxWidth = `${rightW}px`;
  } else {
    right.style.display = "none";
  }

  // Ensure border doesn't clip in small widths
  el.style.padding = "0";
}

function initPpills() {
  document.querySelectorAll(".ppill").forEach(ppillRender);
}

window.addEventListener("load", () => {
  initPpills();
  // Re-render on resize (cheap; only affects .ppill)
  window.addEventListener("resize", () => {
    window.requestAnimationFrame(initPpills);
  });
});

// Also re-render when buttons are added/changed dynamically (ACF editor, filters, etc.)
const ppillObserver = new MutationObserver(() => {
  window.requestAnimationFrame(initPpills);
});
ppillObserver.observe(document.documentElement, { subtree: true, childList: true, characterData: true });

// One extra refresh after all assets settle (images/fonts)
window.addEventListener("load", () => {
  queueScrollTriggerRefresh();
});
